/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeAvatar,
  DimeAvatarFallback,
  DimeAvatarImage,
  DimeButton,
  DimeDropdownMenu,
  DimeDropdownMenuContent,
  DimeDropdownMenuItem,
  DimeDropdownMenuTrigger,
  DimeItem,
  DimeItemActions,
  DimeItemContent,
  DimeItemDescription,
  DimeItemFooter,
  DimeItemGroup,
  DimeItemHeader,
  DimeItemMedia,
  DimeItemTitle,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import {
  ChevronDown,
  ChevronRight,
  ClipboardList,
  ExternalLink,
  FileText,
  Home,
  LayoutDashboard,
  Package,
  Plus,
  Warehouse,
} from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/item')({
  component: ItemDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="rounded-lg border border-border bg-background p-6">
      <div className="mx-auto w-full max-w-md">{children}</div>
    </div>
  );
}

const VARIANTS = [
  { value: 'default', description: 'Transparent border — blends into the page.' },
  { value: 'outline', description: 'Visible border — emphasises each row.' },
  { value: 'muted', description: 'Subtle fill — groups without heavy borders.' },
] as const;

const SIZES = [
  { value: 'default', description: 'Standard row height for most lists.' },
  { value: 'sm', description: 'Compact — use in tight panels or sidebars.' },
  { value: 'xs', description: 'Extra compact — dense lists and dropdowns.' },
] as const;

function ItemDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Item</h1>
      <p className="mb-8 text-muted-foreground">
        A versatile component for displaying content with media, title, description, and actions.
        Compose with <code className="font-mono text-sm">DimeItemMedia</code>,{' '}
        <code className="font-mono text-sm">DimeItemContent</code>, and{' '}
        <code className="font-mono text-sm">DimeItemActions</code>.
      </p>

      <DocsSection title="Default">
        <Preview>
          <DimeItemGroup>
            <DimeItem>
              <DimeItemMedia variant="icon">
                <Package />
              </DimeItemMedia>
              <DimeItemContent>
                <DimeItemTitle>Brake Caliper Assembly</DimeItemTitle>
                <DimeItemDescription>SKU BRK-001 · 48 in stock</DimeItemDescription>
              </DimeItemContent>
              <DimeItemActions>
                <DimeButton variant="outline" size="sm">
                  View
                </DimeButton>
              </DimeItemActions>
            </DimeItem>
          </DimeItemGroup>
        </Preview>
      </DocsSection>

      <DocsSection title="Variant">
        <Preview>
          <DimeItemGroup>
            {VARIANTS.map(({ value }) => (
              <DimeItem key={value} variant={value}>
                <DimeItemMedia variant="icon">
                  <Package />
                </DimeItemMedia>
                <DimeItemContent>
                  <DimeItemTitle>{value.charAt(0).toUpperCase() + value.slice(1)}</DimeItemTitle>
                  <DimeItemDescription>variant="{value}"</DimeItemDescription>
                </DimeItemContent>
              </DimeItem>
            ))}
          </DimeItemGroup>
        </Preview>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Variant</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {VARIANTS.map(({ value, description }) => (
              <tr key={value} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{value}</td>
                <td className="py-2 text-muted-foreground">{description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>

      <DocsSection title="Size">
        <Preview>
          <DimeItemGroup>
            {SIZES.map(({ value }) => (
              <DimeItem key={value} variant="outline" size={value}>
                <DimeItemMedia variant="icon">
                  <Package />
                </DimeItemMedia>
                <DimeItemContent>
                  <DimeItemTitle>{value.charAt(0).toUpperCase() + value.slice(1)}</DimeItemTitle>
                  <DimeItemDescription>size="{value}"</DimeItemDescription>
                </DimeItemContent>
              </DimeItem>
            ))}
          </DimeItemGroup>
        </Preview>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Size</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {SIZES.map(({ value, description }) => (
              <tr key={value} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{value}</td>
                <td className="py-2 text-muted-foreground">{description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>

      <DocsSection title="Icon">
        <Preview>
          <DimeItemGroup>
            {[
              {
                icon: <LayoutDashboard />,
                title: 'Dashboard',
                description: 'Overview of activity',
              },
              { icon: <ClipboardList />, title: 'Orders', description: '12 pending approval' },
              { icon: <Warehouse />, title: 'Warehouse', description: 'Toronto · 1,204 SKUs' },
            ].map(({ icon, title, description }) => (
              <DimeItem key={title} variant="outline">
                <DimeItemMedia variant="icon">{icon}</DimeItemMedia>
                <DimeItemContent>
                  <DimeItemTitle>{title}</DimeItemTitle>
                  <DimeItemDescription>{description}</DimeItemDescription>
                </DimeItemContent>
              </DimeItem>
            ))}
          </DimeItemGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeItemMedia variant="icon"</code> to render a lucide
          icon. The media slot sizes and aligns the icon automatically.
        </p>
      </DocsSection>

      <DocsSection title="Avatar">
        <Preview>
          <DimeItemGroup>
            {[
              { initials: 'AS', name: 'Alice Smith', role: 'Warehouse Manager' },
              { initials: 'BJ', name: 'Bob Johnson', role: 'Procurement Lead' },
              { initials: 'CW', name: 'Carol White', role: 'Finance Analyst' },
            ].map(({ initials, name, role }) => (
              <DimeItem key={name} variant="outline">
                <DimeItemMedia>
                  <DimeAvatar>
                    <DimeAvatarImage src="" alt={name} />
                    <DimeAvatarFallback>{initials}</DimeAvatarFallback>
                  </DimeAvatar>
                </DimeItemMedia>
                <DimeItemContent>
                  <DimeItemTitle>{name}</DimeItemTitle>
                  <DimeItemDescription>{role}</DimeItemDescription>
                </DimeItemContent>
                <DimeItemActions>
                  <DimeButton variant="ghost" size="sm">
                    Message
                  </DimeButton>
                </DimeItemActions>
              </DimeItem>
            ))}
          </DimeItemGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeItemMedia</code> with{' '}
          <code className="font-mono">DimeAvatar</code> to show user avatars alongside content.
        </p>
      </DocsSection>

      <DocsSection title="Image">
        <Preview>
          <DimeItemGroup>
            {[
              { title: 'Engine Mount Bracket', sku: 'ENG-044', qty: 120 },
              { title: 'Front Strut Assembly', sku: 'SUS-017', qty: 56 },
              { title: 'Oil Filter Housing', sku: 'ENG-101', qty: 204 },
            ].map(({ title, sku, qty }) => (
              <DimeItem key={sku} variant="outline">
                <DimeItemMedia variant="image">
                  <div className="flex size-full items-center justify-center bg-muted text-xs text-muted-foreground">
                    {sku}
                  </div>
                </DimeItemMedia>
                <DimeItemContent>
                  <DimeItemTitle>{title}</DimeItemTitle>
                  <DimeItemDescription>
                    {sku} · {qty} in stock
                  </DimeItemDescription>
                </DimeItemContent>
              </DimeItem>
            ))}
          </DimeItemGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeItemMedia variant="image"</code> for a fixed-size
          image thumbnail. The slot clips and covers the image automatically.
        </p>
      </DocsSection>

      <DocsSection title="Group">
        <Preview>
          <DimeItemGroup className="max-w-sm">
            {[
              { initials: 'AS', name: 'alice', email: 'alice@example.com' },
              { initials: 'BJ', name: 'bob', email: 'bob@example.com' },
              { initials: 'CW', name: 'carol', email: 'carol@example.com' },
            ].map(({ initials, name, email }) => (
              <DimeItem key={name} variant="outline">
                <DimeItemMedia>
                  <DimeAvatar>
                    <DimeAvatarImage src="" alt={name} className="grayscale" />
                    <DimeAvatarFallback>{initials}</DimeAvatarFallback>
                  </DimeAvatar>
                </DimeItemMedia>
                <DimeItemContent className="gap-1">
                  <DimeItemTitle>{name}</DimeItemTitle>
                  <DimeItemDescription>{email}</DimeItemDescription>
                </DimeItemContent>
                <DimeItemActions>
                  <DimeButton
                    variant="ghost"
                    size="icon"
                    className="rounded-full"
                    aria-label={`Add ${name}`}
                  >
                    <Plus />
                  </DimeButton>
                </DimeItemActions>
              </DimeItem>
            ))}
          </DimeItemGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap items in <code className="font-mono">DimeItemGroup</code> for consistent spacing. No
          separators needed — the gap between items is handled automatically.
        </p>
      </DocsSection>

      <DocsSection title="Header">
        <Preview>
          <DimeItemGroup>
            <DimeItem variant="outline">
              <DimeItemHeader>
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Recent orders
                </span>
                <DimeButton variant="ghost" size="xs">
                  View all
                </DimeButton>
              </DimeItemHeader>
              <DimeItemMedia variant="icon">
                <FileText />
              </DimeItemMedia>
              <DimeItemContent>
                <DimeItemTitle>PO-2048</DimeItemTitle>
                <DimeItemDescription>Acme Manufacturing · $12,400</DimeItemDescription>
              </DimeItemContent>
            </DimeItem>
            <DimeItem variant="outline">
              <DimeItemMedia variant="icon">
                <FileText />
              </DimeItemMedia>
              <DimeItemContent>
                <DimeItemTitle>PO-2047</DimeItemTitle>
                <DimeItemDescription>Global Parts Co. · $8,900</DimeItemDescription>
              </DimeItemContent>
            </DimeItem>
          </DimeItemGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeItemHeader</code> spans the full width of the item and
          renders above the media and content — useful for section labels or in-row actions.
        </p>
      </DocsSection>

      <DocsSection title="Link">
        <Preview>
          <div className="flex w-full max-w-md flex-col gap-4">
            <DimeItem asChild>
              <a href="/design-system/item">
                <DimeItemContent>
                  <DimeItemTitle>Visit our documentation</DimeItemTitle>
                  <DimeItemDescription>
                    Learn how to get started with our components.
                  </DimeItemDescription>
                </DimeItemContent>
                <DimeItemActions>
                  <ChevronRight className="size-4" />
                </DimeItemActions>
              </a>
            </DimeItem>
            <DimeItem variant="outline" asChild>
              <a href="https://example.com" target="_blank" rel="noopener noreferrer">
                <DimeItemContent>
                  <DimeItemTitle>External resource</DimeItemTitle>
                  <DimeItemDescription>
                    Opens in a new tab with security attributes.
                  </DimeItemDescription>
                </DimeItemContent>
                <DimeItemActions>
                  <ExternalLink className="size-4" />
                </DimeItemActions>
              </a>
            </DimeItem>
            <DimeItem variant="outline" asChild>
              <a href="/design-system/item">
                <DimeItemMedia variant="icon">
                  <Home />
                </DimeItemMedia>
                <DimeItemContent>
                  <DimeItemTitle>Dashboard</DimeItemTitle>
                  <DimeItemDescription>Overview of your account and activity.</DimeItemDescription>
                </DimeItemContent>
              </a>
            </DimeItem>
          </div>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">asChild</code> to render the item as an{' '}
          <code className="font-mono">&lt;a&gt;</code> element. Hover and focus states are forwarded
          to the anchor. Set{' '}
          <code className="font-mono">target="_blank" rel="noopener noreferrer"</code> for external
          links.
        </p>
      </DocsSection>

      <DocsSection title="Dropdown">
        <Preview>
          <DimeDropdownMenu>
            <DimeDropdownMenuTrigger asChild>
              <DimeButton variant="outline">
                Assign to <ChevronDown />
              </DimeButton>
            </DimeDropdownMenuTrigger>
            <DimeDropdownMenuContent className="w-52" align="start">
              {[
                { initials: 'AS', name: 'alice', email: 'alice@example.com' },
                { initials: 'BJ', name: 'bob', email: 'bob@example.com' },
                { initials: 'CW', name: 'carol', email: 'carol@example.com' },
              ].map(({ initials, name, email }) => (
                <DimeDropdownMenuItem key={name} className="p-0">
                  <DimeItem size="xs" className="w-full px-2 py-1.5">
                    <DimeItemMedia>
                      <DimeAvatar className="size-6">
                        <DimeAvatarImage src="" alt={name} className="grayscale" />
                        <DimeAvatarFallback className="text-xs">{initials}</DimeAvatarFallback>
                      </DimeAvatar>
                    </DimeItemMedia>
                    <DimeItemContent className="gap-0">
                      <DimeItemTitle>{name}</DimeItemTitle>
                      <DimeItemDescription className="leading-none">{email}</DimeItemDescription>
                    </DimeItemContent>
                  </DimeItem>
                </DimeDropdownMenuItem>
              ))}
            </DimeDropdownMenuContent>
          </DimeDropdownMenu>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeItem</code> inside{' '}
          <code className="font-mono">DimeDropdownMenuItem</code> to render rich content — avatars,
          titles, and descriptions — inside a menu.
        </p>
      </DocsSection>

      <DocsSection title="Footer">
        <Preview>
          <DimeItemGroup>
            <DimeItem variant="outline">
              <DimeItemMedia variant="icon">
                <FileText />
              </DimeItemMedia>
              <DimeItemContent>
                <DimeItemTitle>Q2 Procurement Report</DimeItemTitle>
                <DimeItemDescription>Generated 2026-06-01 · 4.2 MB</DimeItemDescription>
              </DimeItemContent>
              <DimeItemFooter>
                <span className="text-xs text-muted-foreground">Shared with 3 people</span>
                <div className="flex gap-1">
                  <DimeButton variant="outline" size="xs">
                    Download
                  </DimeButton>
                  <DimeButton variant="ghost" size="xs">
                    Share
                  </DimeButton>
                </div>
              </DimeItemFooter>
            </DimeItem>
          </DimeItemGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeItemFooter</code> spans the full width below the media and
          content — use it for metadata, secondary actions, or status rows.
        </p>
      </DocsSection>
    </div>
  );
}
